import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/admin/Form";

export default function MediaLibrary() {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const ref = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/files");
            setFiles(data);
        } catch { toast.error("Failed to load media"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const upload = async (e) => {
        const fls = e.target.files;
        if (!fls || fls.length === 0) return;
        setUploading(true);
        try {
            for (const f of fls) {
                const fd = new FormData();
                fd.append("file", f);
                await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(`${fls.length} file(s) uploaded.`);
            await load();
        } catch { toast.error("Upload failed"); }
        finally { setUploading(false); if (ref.current) ref.current.value = ""; }
    };

    const copy = (url) => {
        navigator.clipboard.writeText(url);
        toast.success("URL copied.");
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this file?")) return;
        try {
            await api.delete(`/admin/files/${id}`);
            await load();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="media-library">
            <PageHeader subtitle="Media Library" title="Upload images and videos" />

            <button
                onClick={() => ref.current?.click()}
                disabled={uploading}
                className="btn-pill btn-pill-coffee mb-8"
                data-testid="upload-button"
            >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "Uploading…" : "Upload Files"}
            </button>
            <input ref={ref} type="file" multiple accept="image/*,video/*" onChange={upload} className="hidden" data-testid="upload-input" />

            {loading ? (
                <div className="tracked text-coffee/50 text-[11px]">Loading…</div>
            ) : files.length === 0 ? (
                <div className="font-editorial font-bold text-coffee/50 text-2xl">No files yet. Upload your first photo.</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {files.map((f) => {
                        const isVideo = (f.content_type || "").startsWith("video");
                        return (
                            <div key={f.id} className="group relative bg-white border border-coffee/10 overflow-hidden" data-testid={`file-${f.id}`}>
                                <div className="aspect-square bg-coffee/5 overflow-hidden">
                                    {isVideo ? (
                                        <video src={fileUrl(f.url)} className="w-full h-full object-cover" muted />
                                    ) : (
                                        <img src={fileUrl(f.url)} alt={f.original_filename} className="w-full h-full object-cover" />
                                    )}
                                </div>
                                <div className="p-2 text-[10px] font-mono text-coffee/60 truncate">{f.original_filename}</div>
                                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => copy(f.url)} className="bg-coffee text-cream p-2" aria-label="copy url" data-testid={`copy-${f.id}`}>
                                        <Copy size={12} />
                                    </button>
                                    <button onClick={() => remove(f.id)} className="bg-coffee text-cream p-2 hover:bg-destructive" aria-label="delete" data-testid={`del-${f.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
